import React, { useMemo, useState } from 'react';
import { Pipette } from 'lucide-react';
import styles from './gerador.module.scss';
import { readAndResizeImage } from './utils.ts';

type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> };

function toHex6(hex: string): string {
    const value = hex.trim();
    if (/^#[0-9a-fA-F]{8}$/.test(value)) return value.slice(0, 7).toLowerCase();
    if (/^#[0-9a-fA-F]{6}$/.test(value)) return value.toLowerCase();
    return value;
}

type ColorFieldProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    hint?: string;
};

export const ColorField: React.FC<ColorFieldProps> = ({ label, value, onChange, hint }) => {
    const canPick = useMemo(() => typeof window !== 'undefined' && 'EyeDropper' in window, []);
    const [picking, setPicking] = useState(false);

    const pickFromScreen = async () => {
        const Ctor = (window as Window & { EyeDropper?: EyeDropperCtor }).EyeDropper;
        if (!Ctor) return;
        setPicking(true);
        try {
            const result = await new Ctor().open();
            onChange(toHex6(result.sRGBHex));
        } catch {
            // cancelado
        } finally {
            setPicking(false);
        }
    };

    return (
        <div className={`${styles.field} ${styles.colorField}`}>
            <span className={styles.fieldLabel}>{label}</span>
            <div className={styles.colorRow}>
                <input
                    type="color"
                    value={value.length === 7 ? value : '#000000'}
                    onChange={(e) => onChange(e.target.value)}
                    aria-label={label}
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    spellCheck={false}
                    aria-label={`${label} em hexadecimal`}
                />
                {canPick ? (
                    <button
                        type="button"
                        className={styles.eyedropperBtn}
                        onClick={() => void pickFromScreen()}
                        disabled={picking}
                        title="Capturar cor da prévia ou de qualquer ponto da tela"
                        aria-label="Conta-gotas: capturar cor da prévia"
                    >
                        <Pipette size={15} />
                    </button>
                ) : null}
            </div>
            {hint ? <span className={styles.hint}>{hint}</span> : null}
        </div>
    );
};

type RangeFieldProps = {
    label: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    suffix?: string;
    onChange: (value: number) => void;
};

export const RangeField: React.FC<RangeFieldProps> = ({
    label,
    value,
    min,
    max,
    step = 1,
    suffix = 'px',
    onChange,
}) => (
    <label className={styles.field}>
        <span className={styles.fieldLabel}>
            {label}
            <strong>{value}{suffix}</strong>
        </span>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
        />
    </label>
);

type SegmentOption<T extends string> = {
    value: T;
    label: React.ReactNode;
    title?: string;
};

type SegmentedProps<T extends string> = {
    label: string;
    value: T;
    options: SegmentOption<T>[];
    onChange: (value: T) => void;
    tour?: string;
    compact?: boolean;
};

export function Segmented<T extends string>({
    label,
    value,
    options,
    onChange,
    tour,
    compact,
}: SegmentedProps<T>) {
    return (
        <div className={`${styles.field} ${compact ? styles.fieldCompact : ''}`} data-tour={tour}>
            <span className={styles.fieldLabel}>{label}</span>
            <div
                className={`${styles.segmented} ${compact ? styles.segmentedCompact : ''}`}
                role="group"
                aria-label={label}
            >
                {options.map((option) => {
                    const accessible = option.title ?? (typeof option.label === 'string' ? option.label : undefined);
                    return (
                        <button
                            key={option.value}
                            type="button"
                            className={value === option.value ? styles.segmentActive : undefined}
                            onClick={() => onChange(option.value)}
                            title={accessible}
                            aria-label={accessible}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

type ImageFieldProps = {
    label: string;
    value: string | null;
    onChange: (value: string | null) => void;
    hint?: string;
    optional?: boolean;
    maxEdge?: number;
    tour?: string;
    previewRatio?: string;
    compact?: boolean;
};

export const ImageField: React.FC<ImageFieldProps> = ({
    label,
    value,
    onChange,
    hint,
    optional,
    maxEdge = 1200,
    tour,
    previewRatio,
    compact,
}) => {
    const inputId = React.useId();

    const handleFiles = async (file: File | undefined) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) return;
        const dataUrl = await readAndResizeImage(file, maxEdge);
        onChange(dataUrl);
    };

    return (
        <div className={`${styles.field} ${compact ? styles.fieldCompact : ''}`} data-tour={tour}>
            <span className={styles.fieldLabel}>
                {label}
                {optional ? <em>opcional</em> : <em>obrigatório</em>}
            </span>
            <label
                htmlFor={inputId}
                className={[
                    styles.dropzone,
                    previewRatio ? styles.dropzoneRatio : '',
                    compact ? styles.dropzoneCompact : '',
                ].filter(Boolean).join(' ')}
                style={previewRatio ? { aspectRatio: previewRatio } : undefined}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    void handleFiles(e.dataTransfer.files[0]);
                }}
            >
                {value ? (
                    <img src={value} alt="" />
                ) : (
                    <span>{compact ? 'Enviar' : 'Clique ou solte a imagem aqui'}</span>
                )}
            </label>
            <input
                id={inputId}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                    void handleFiles(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
            <div className={styles.imageActions}>
                <label htmlFor={inputId} className={styles.ghostBtn}>
                    {compact ? (value ? 'Trocar' : 'Enviar') : 'Escolher arquivo'}
                </label>
                {value && optional ? (
                    <button type="button" className={styles.ghostBtn} onClick={() => onChange(null)}>
                        Remover
                    </button>
                ) : null}
            </div>
            {hint ? <span className={styles.hint}>{hint}</span> : null}
        </div>
    );
};

type AccordionProps = {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
    tour?: string;
};

export const Accordion: React.FC<AccordionProps> = ({ title, children, defaultOpen = true, tour }) => {
    const [open, setOpen] = React.useState(defaultOpen);

    React.useEffect(() => {
        const onTour = (event: Event) => {
            const target = (event as CustomEvent<string>).detail;
            if (target && target === tour) setOpen(true);
        };
        window.addEventListener('gerador-tour-step', onTour);
        return () => window.removeEventListener('gerador-tour-step', onTour);
    }, [tour]);

    return (
        <div className={styles.accordion} data-tour={tour}>
            <button type="button" className={styles.accordionHead} onClick={() => setOpen((v) => !v)}>
                <span>{title}</span>
                <span aria-hidden>{open ? '−' : '+'}</span>
            </button>
            {open ? <div className={styles.accordionBody}>{children}</div> : null}
        </div>
    );
};
